import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_BASE_URL } from '../config';

const AuthContext = createContext(null);

// Helper functions for role-specific storage
const getStorageKey = (role, key) => `${role}_${key}`;

const storeUserData = (userData) => {
  const role = userData.type;
  localStorage.setItem(getStorageKey(role, 'user'), JSON.stringify(userData));
  localStorage.setItem(getStorageKey(role, 'token'), userData.access_token);

  // Also store current active role for quick access
  localStorage.setItem('active_role', role);
  localStorage.setItem('active_user', JSON.stringify(userData));
};

const getUserData = () => {
  const activeRole = localStorage.getItem('active_role');
  if (activeRole) {
    const userData = localStorage.getItem(getStorageKey(activeRole, 'user'));
    return userData ? JSON.parse(userData) : null;
  }
  return null;
};

const clearUserData = (role) => {
  if (role) {
    localStorage.removeItem(getStorageKey(role, 'user'));
    localStorage.removeItem(getStorageKey(role, 'token'));
  }
  localStorage.removeItem('active_role');
  localStorage.removeItem('active_user');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = getUserData();
    if (storedUser) {
      setUser(storedUser);
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      // First try admin login
      try {
        const adminResponse = await axios.post(
          `${BACKEND_BASE_URL}/api/admin/login`,
          { email, password }
        );

        if (adminResponse.data.message === 'Login successful') {
          const userData = {
            ...adminResponse.data,
            type: 'admin'
          };
          setUser(userData);
          storeUserData(userData);
          return { success: true, user: userData };
        }
      } catch (adminError) {
        // If admin login fails, try client login
        try {
          const clientResponse = await axios.post(
            `${BACKEND_BASE_URL}/api/client/login`,
            { email, password }
          );

          if (clientResponse.data.message === 'Login successful') {
            const userData = {
              ...clientResponse.data,
              type: 'client'
            };
            setUser(userData);
            storeUserData(userData);
            return { success: true, user: userData };
          }
        } catch (clientError) {
          // If client login fails, try field officer login
          const fieldOfficerResponse = await axios.post(
            `${BACKEND_BASE_URL}/api/field-officer/login`,
            { email, password }
          );

          if (fieldOfficerResponse.data.message === 'Login successful') {
            const userData = {
              ...fieldOfficerResponse.data,
              type: 'field_officer'
            };
            setUser(userData);
            storeUserData(userData);
            return { success: true, user: userData };
          }
        }
      }

      throw new Error('Invalid credentials');
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Invalid credentials');
    }
  };

  const logout = () => {
    const currentRole = user?.type;
    setUser(null);
    clearUserData(currentRole);
  };

  // Helper function to get token for current user
  const getToken = () => {
    const activeRole = localStorage.getItem('active_role');
    if (activeRole) {
      return localStorage.getItem(getStorageKey(activeRole, 'token'));
    }
    return null;
  };

  const value = {
    user,
    login,
    logout,
    loading,
    getToken,
    isAuthenticated: !!user,
    isAdmin: user?.type === 'admin',
    isClient: user?.type === 'client',
    isFieldOfficer: user?.type === 'field_officer'
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 